import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchRetailPricing,
  saveRetailPricing,
  deleteRetailPricing,
  bulkApplyRetailPricing,
} from '@/api/retailPricing'
import type {
  BulkApplyPricingRequest,
  SaveRetailPricingRequest,
} from '@/api/retailPricing'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import { AxiosError } from 'axios'

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.detail) {
    const detail = error.response.data.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0]?.msg ?? 'An error occurred'
    }
  }
  return 'An error occurred'
}

export function useRetailPricing(
  planId: string | undefined,
  versionId: string | undefined,
  lineId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.retailPricing.byLine(
      planId ?? '',
      versionId ?? '',
      lineId ?? ''
    ),
    queryFn: () => fetchRetailPricing(planId!, versionId!, lineId!),
    enabled: !!planId && !!versionId && !!lineId,
  })
}

export function useSaveRetailPricing(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      lineId,
      body,
    }: {
      lineId: string
      body: SaveRetailPricingRequest
    }) => saveRetailPricing(planId, versionId, lineId, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.retailPricing.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      // Pricing model changes recompute target retail/cost/margin cells
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
      toast.success('Pricing model saved successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

export function useBulkSaveRetailPricing(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: BulkApplyPricingRequest) =>
      bulkApplyRetailPricing(planId, versionId, body),
    onSuccess: (data, variables) => {
      const ids = new Set(variables.line_ids)
      queryClient.invalidateQueries({
        predicate: query => {
          const key = query.queryKey
          return (
            Array.isArray(key) &&
            key[0] === 'retail-pricing' &&
            key[1] === planId &&
            key[2] === versionId &&
            typeof key[3] === 'string' &&
            ids.has(key[3])
          )
        },
      })
      // Pricing model changes recompute target retail/cost/margin cells
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
      if (data.failed === 0) {
        toast.success(
          `Pricing model applied to ${data.succeeded} line${data.succeeded === 1 ? '' : 's'}`
        )
      } else {
        toast.error(`Applied to ${data.succeeded}; failed for ${data.failed}`)
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to apply pricing model'))
    },
  })
}

export function useDeleteRetailPricing(planId: string, versionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ lineId }: { lineId: string }) =>
      deleteRetailPricing(planId, versionId, lineId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.retailPricing.byLine(
          planId,
          versionId,
          variables.lineId
        ),
      })
      // Removing the pricing model resets target retail/cost/margin cells
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
      toast.success('Pricing model removed successfully')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}
